import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Listbox, Menu, Switch, Transition } from '@headlessui/react';
import moment from 'moment';
import Compressor from 'compressorjs';
import { PaperClipIcon, DocumentIcon } from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import {
	CheckCircleIcon,
	ChevronDownIcon,
	PencilIcon,
} from '@heroicons/react/24/outline';

import {
	commentOnTicket,
	deleteTicket,
	getTicket,
	updateTicket,
	addTicketDocuments,
} from '../api';
import { getTeam } from '../api/company';

import Modal from '../components/Shell/Modal';
import { classNames, getUserFullName } from '../components/helpers/functions';
import Badge from '../components/Badge';

import {
	AttributeRow,
	TemplateDropdown,
	TemplateField,
	TemplateTextarea,
} from '../components/Attributes/Attributes_old';
import Button from '../components/Button';

import LoadingSkeleton from '../components/Shell/LoadingSkeleton';
import { AvatarGenerated } from '../components/Avatar';
import Tabs from '../components/Tabs';

import useModal from '../hooks/useModal';
import useLocal from '../hooks/useLocal';
import useAsync from '../hooks/useAsync';

const MODAL_NAME = 'ticketOverview';

const Editable = ({ children, disabled, onClick, cursor = 'pointer' }) => (
	<div
		className={classNames(
			`group flex`,
			disabled ? 'cursor-default' : `cursor-${cursor}`,
		)}
		onClick={() => typeof onClick === 'function' && !disabled && onClick()}
	>
		{children}
		{!disabled && (
			<div className="ml-2 opacity-0 group-hover:opacity-100 transistion duration-200 ease-in-out flex items-center">
				<PencilIcon className="w-3 h-3 text-gray-400" />
			</div>
		)}
	</div>
);

const STATUS_OPTIONS = [
	{
		name: 'Incoming',
		key: 'incoming',
		description:
			'Initial ticket status.  This is used to keep track of new tickets',
	},
	{
		name: 'In Progress',
		key: 'inprogress',
		description: 'Ticket is being actively worked on',
	},
	{
		name: 'Done',
		key: 'done',
		description: 'Ticket has been successfully completed',
	},
	{
		name: 'Closed',
		key: 'closed',
		description: 'Ticket is not completed and closed',
	},
];

const generateLineItem = (ticket) => {
	const name = `${ticket?.addedBy?.firstName} ${ticket?.addedBy?.lastName}`;
	const updated =
		ticket?.data_type === 'object'
			? ticket?.data
				? JSON.parse(ticket?.data)
				: null
			: ticket?.data;

	if (ticket?.type === 'ticket.created') {
		return (
			<>
				<span className="font-medium text-gray-900">{name}</span> created the
				ticket
			</>
		);
	}
	if (ticket?.type === 'ticket.done') {
		return (
			<>
				<span className="font-medium text-gray-900">{name}</span> marked this
				ticket as Done
			</>
		);
	}
	if (ticket?.type === 'ticket.closed') {
		return (
			<>
				<span className="font-medium text-gray-900">{name}</span> closed this
				ticket
			</>
		);
	}

	if (
		ticket?.type === 'severity.updated' ||
		ticket?.type === 'status.updated' ||
		ticket?.type === 'title.updated' ||
		ticket?.type === 'description.updated'
	) {
		return (
			<>
				<span className="font-medium text-gray-900">{name}</span>
				{` updated the ${updated.key}`}
			</>
		);
	}

	if (ticket?.type === 'assignedTo.updated') {
		return (
			<>
				<span className="font-medium text-gray-900">{name}</span>
				{` assigned this ticket`}
			</>
		);
	}
	return null;
};

const TicketOverview = () => {
	const descriptionTextareaRef = useRef();

	const navigate = useNavigate();

	const { open, setClose } = useModal(MODAL_NAME);

	const { setOpen: onOpenDeleteModal, setClose: onCloseDeleteModal } =
		useModal('deleteModal');

	const [form, setForm] = useState({});
	const [isEditingModalOpen, setEditingModalOpen] = useState(false);
	const [isUploading, setIsUploading] = useState(false);

	const { get } = useLocal();
	const user = get('user');

	const handleClose = (path) => {
		setClose();
		if (typeof open?.onClose === 'function') {
			open?.onClose(path);
		}
	};

	// Get Ticket
	const {
		value: ticket,
		status,
		execute: refreshTicket,
	} = useAsync(getTicket, { immediate: false });

	const { execute: onComment, status: commentStatus } = useAsync(
		commentOnTicket,
		{ immediate: false },
	);

	const { execute: onUpdate, status: updating } = useAsync(updateTicket, {
		immediate: false,
	});

	const { value: team, status: gettingTeam } = useAsync(getTeam);

	const { execute: onDeleteTicket } = useAsync(deleteTicket, {
		immediate: false,
	});

	const { execute: onUploadDocuments } = useAsync(addTicketDocuments, {
		immediate: false,
	});

	// Upload a single file immediately
	const uploadFile = (file) => {
		setIsUploading(true);
		onUploadDocuments(
			{
				id: open?.id,
				files: [file],
			},
			() => {
				setIsUploading(false);
				refreshTicket({ id: open?.id }, null, { silent: true });
			},
		);
	};

	// File upload handler - uploads immediately
	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		if (file.type.startsWith('image/')) {
			setIsUploading(true);
			new Compressor(file, {
				quality: 0.6,
				maxWidth: 1920,
				maxHeight: 1920,
				success: (compressedBlob) => {
					const compressedFile = new File([compressedBlob], file.name, {
						type: compressedBlob.type,
					});
					uploadFile(compressedFile);
				},
				error: () => {
					uploadFile(file);
				},
			});
		} else {
			uploadFile(file);
		}

		e.target.value = '';
	};

	useEffect(() => {
		if (open?.id) {
			refreshTicket({ id: open?.id });
		}
	}, [open?.id]);

	useEffect(() => {
		if (ticket) {
			setForm({
				...form,
				description: ticket?.description,
				subject: ticket?.subject,
				status: ticket?.status,
				severity: ticket?.severity,
				assignedTo: ticket?.assigned_to || 'unassigned',
			});
			descriptionTextareaRef?.current?.focus();
		}
	}, [ticket]);

	const SeverityMapping = {
		minor: {
			label: 'Minor',
			value: 'minor',
			badgeValue: 'caution',
		},
		major: {
			label: 'Major',
			value: 'major',
			badgeValue: 'warning',
		},
		critical: {
			label: 'Critical',
			value: 'critical',
			badgeValue: 'error',
		},
	};

	const ticketSeverity = SeverityMapping[ticket?.severity];

	const isUpdateDisabled = () =>
		!form[isEditingModalOpen] ||
		(isEditingModalOpen &&
			ticket &&
			ticket[isEditingModalOpen] === form[isEditingModalOpen]);

	const modalTitle = `${ticket?.ticketCode ? `${ticket?.ticketCode}: ` : ''} ${
		ticket?.subject || ticket?.consumable || 'No Title'
	}`;

	const isBare = !!open?.isBare;
	return (
		<>
			<Modal
				open={!!open}
				key={MODAL_NAME}
				name={MODAL_NAME}
				onClose={() => handleClose()}
				size="4xl"
			>
				{status === 'pending' ? (
					<LoadingSkeleton />
				) : (
					<div className="grid grid-cols-3 gap-x-6">
						<div className="col-span-1 sm:col-span-2">
							<div className="text-lg font-medium border-b mb-3 pb-4">
								<div className="flex items-center">
									<Editable
										cursor="type"
										disabled={ticket?.type === 'consumable'}
										onClick={() => setEditingModalOpen('subject')}
									>
										{modalTitle}
									</Editable>
									{ticket?.severity && (
										<div
											className="mx-2 flex items-center"
											onClick={() => setEditingModalOpen('severity')}
										>
											<Badge
												status={ticketSeverity?.badgeValue}
												size={'small'}
												hasDot={false}
												className="cursor-pointer"
												isBeta
											>
												{`Severity: ${ticketSeverity?.label}`}
											</Badge>
										</div>
									)}
								</div>

								<div className="text-xs text-gray-500 mt-1.5 font-normal">
									{
										{
											general: 'General',
											consumable: `Consumable Request: ${
												(ticket?.consumableUrgency === 'low' &&
													'Running low') ||
												(ticket?.consumableUrgency === 'immediately' &&
													'Need Immediately') ||
												(ticket?.consumable_urgency === 'date' &&
													`Need by: ${moment(ticket?.consumableDate).format(
														'MMM Do YYYY',
													)}`)
											}`,
											asset: 'Asset',
											incident: 'Incident',
										}[ticket?.type]
									}
								</div>
							</div>

							<div
								className="text-xs text-gray-600 my-6"
								onClick={() => setEditingModalOpen('description')}
							>
								<AttributeRow name={'Description'}>
									<Editable cursor="type">
										{ticket?.description || (
											<span className="text-indigo-600 hover:text-indigo-500 cursor-pointer">
												Add description
											</span>
										)}
									</Editable>
								</AttributeRow>
							</div>

							<div className="mb-3">
								{ticket?.asset && !isBare && (
									<div
										className="cursor-pointer my-3 mb-5 border rounded-md p-4 text-xs"
										onClick={() => {
											handleClose(`/assets/${ticket?.asset?.id}`);
										}}
									>
										<div class="flex items-center">
											<AvatarGenerated
												type="asset"
												id={ticket?.asset?.id}
												size="xs"
											/>
											<div class="ml-4">
												<div class="font-medium">{`${
													ticket?.asset?.manufacturerName ||
													ticket?.asset?.manufacturer?.name
												} ${
													ticket?.asset?.modelName || ticket?.asset?.model?.name
												}`}</div>
												<div class="text-gray-400 font-normal text-xxs">{`${ticket?.asset?.serialNumber}`}</div>
											</div>
										</div>
									</div>
								)}

								{ticket?.location && !isBare && (
									<div className="mt-3">
										<div className="text-xs font-medium">Location:</div>
										<div className="text-xs text-gray-500 mt-1.5">
											<div
												className="cursor-pointer my-3 mb-5 border rounded-md p-4 text-xs"
												onClick={() => {
													handleClose(
														`/assets/all?location=${ticket?.location?.id}`,
													);
												}}
											>
												<div class="flex items-center">
													<div class="font-medium">{`${ticket?.location?.name}`}</div>
												</div>
											</div>
										</div>
									</div>
								)}
							</div>

							<section aria-labelledby="attachments-title">
								<div>
									<div className="divide-y divide-gray-200">
										<div className="pb-2">
											<h2
												id="attachments-title"
												className="text-xs font-medium text-gray-900"
											>
												Attachments
											</h2>
										</div>
									</div>
								</div>

								{/* Existing attachments */}
								{ticket?.documents?.length > 0 && (
									<ul
										role="list"
										className="divide-y divide-gray-200 rounded-md border border-gray-200 mb-4"
									>
										{ticket?.documents.map((attachment) => (
											<li
												key={attachment.name}
												className="flex items-center justify-between py-3 pl-3 pr-4 text-xxs cursor-pointer"
												onClick={() => {
													const win = window.open(
														attachment?.location,
														'_blank',
													);
													win.focus();
												}}
											>
												<div className="flex w-0 flex-1 items-center">
													<PaperClipIcon
														className="h-5 w-5 flex-shrink-0 text-gray-400"
														aria-hidden="true"
													/>
													<span className="ml-2 w-0 flex-1 truncate text-left text-xs">
														{attachment.name}
													</span>
												</div>
												<div className="ml-4 flex-shrink-0">
													<a className="font-medium text-blue-600 hover:text-blue-500">
														View
													</a>
												</div>
											</li>
										))}
									</ul>
								)}

								{/* File upload drop zone */}
								<div className="mt-4 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
									<DocumentIcon className="mx-auto h-8 w-8 text-gray-400" />
									<div className="mt-2 text-sm text-gray-600">
										<label
											htmlFor="ticket-attachment-upload"
											className={classNames(
												'relative cursor-pointer rounded-md bg-white font-medium focus-within:outline-none',
												isUploading
													? 'text-gray-400 cursor-not-allowed'
													: 'text-indigo-600 hover:text-indigo-500',
											)}
										>
											<span>{isUploading ? 'Uploading...' : 'Upload files'}</span>
											<input
												id="ticket-attachment-upload"
												name="ticket-attachment-upload"
												type="file"
												className="sr-only"
												accept="image/jpeg, image/png, application/pdf, .doc, .docx, .xls, .xlsx"
												onChange={handleFileUpload}
												disabled={isUploading}
											/>
										</label>
										{!isUploading && (
											<span className="text-gray-500"> or drag and drop</span>
										)}
									</div>
									<p className="text-xs text-gray-500 mt-1">
										PNG, JPG, PDF up to 10MB
									</p>
								</div>
							</section>

							<section
								aria-labelledby="activity-title"
								className="mt-8 xl:mt-10"
							>
								<Tabs
									tabs={[
										...(!isBare
											? [
													{
														name: 'Comments',
														key: 'comments',
														count: ticket?.activities?.filter(
															(activity) => activity.type === 'comment.created',
														)?.length,
													},
												]
											: []),
										{
											name: 'Activity',
											key: 'activities',
											count: ticket?.activities?.filter(
												(activity) => activity.type !== 'comment.created',
											)?.length,
										},
										{
											name: 'Meta',
											key: 'meta',
											showZero: false,
										},
									]}
									showZero
								>
									{(tab) => (
										<>
											<div>
												<div className="divide-y divide-gray-200">
													<div className="lg:col-start-3">
														<ul
															role="list"
															className={classNames(
																'space-y-6',
																tab === 'activities' ? 'my-6' : 'mt-6',
															)}
														>
															{tab === 'comments' && (
																<>
																	{ticket?.activities
																		?.filter(
																			(activity) =>
																				activity.type === 'comment.created',
																		)
																		?.map((activityItem, activityItemIdx) => (
																			<li
																				key={activityItem.id}
																				className="relative flex gap-x-4"
																			>
																				<div
																					className={classNames(
																						activityItemIdx ===
																							ticket?.activities?.length - 1
																							? 'h-6'
																							: '-bottom-6',
																						'absolute left-0 top-0 flex w-6 justify-center',
																					)}
																				>
																					<div className="w-px bg-gray-200" />
																				</div>

																				<>
																					<AvatarGenerated
																						id={getUserFullName(
																							activityItem?.addedBy,
																						)}
																						size="xxs"
																						className="relative mt-3 h-6 w-6 flex-none rounded-full bg-gray-50"
																					/>
																					<div className="flex-auto rounded-md px-4 py-2 ring-1 ring-inset ring-gray-200">
																						<div className="flex justify-between gap-x-4">
																							<div className="py-0.5 text-xs leading-5 text-gray-500">
																								<span className="font-medium text-gray-900">
																									{`${activityItem?.addedBy?.firstName} ${activityItem?.addedBy?.lastName}`}
																								</span>{' '}
																								commented
																							</div>
																							<time
																								dateTime={
																									activityItem?.createdAt
																								}
																								className="flex-none py-0.5 text-xs leading-5 text-gray-500"
																							>
																								{moment(
																									activityItem?.createdAt,
																								).fromNow()}
																							</time>
																						</div>
																						<p className="text-xs text-gray-500 mt-1">
																							{activityItem.data}
																						</p>
																					</div>
																				</>
																			</li>
																		))}

																	<div className="mt-6">
																		<div className="flex space-x-3">
																			<div className="flex-shrink-0">
																				<div className="relative">
																					<AvatarGenerated
																						id={getUserFullName(user)}
																						size="xs"
																					/>
																				</div>
																			</div>
																			<div className="min-w-0 flex-1">
																				<TemplateTextarea
																					placeholder="Leave a comment"
																					dataIndex="comment"
																					onChange={(index, comment) =>
																						setForm({ ...form, comment })
																					}
																					value={form?.comment}
																				/>
																				<div className="mt-6 flex items-center justify-end space-x-4">
																					<Button
																						onClick={() =>
																							onComment(
																								{
																									id: open?.id,
																									comment: form?.comment,
																								},
																								() => {
																									setForm({
																										...form,
																										comment: '',
																									});
																									refreshTicket(
																										{ id: open?.id },
																										null,
																										{
																											silent: true,
																										},
																									);
																								},
																							)
																						}
																						disabled={!form?.comment}
																					>
																						Add Comment
																					</Button>
																				</div>
																			</div>
																		</div>
																	</div>
																</>
															)}

															{tab === 'activities' && (
																<>
																	{ticket?.activities
																		?.filter(
																			(activity) =>
																				activity?.type !== 'comment.created',
																		)
																		.map((activityItem, activityItemIdx) => (
																			<li
																				key={activityItem.id}
																				className="relative flex gap-x-4"
																			>
																				<div
																					className={classNames(
																						activityItemIdx ===
																							ticket?.activities?.length - 1
																							? 'h-6'
																							: '-bottom-6',
																						'absolute left-0 top-0 flex w-6 justify-center',
																					)}
																				>
																					<div className="w-px bg-gray-200" />
																				</div>

																				<>
																					<div className="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
																						{/* CIRCLE */}
																						{activityItem.type === 'paid' ? (
																							<CheckCircleIcon
																								className="h-6 w-6 text-indigo-600"
																								aria-hidden="true"
																							/>
																						) : (
																							<div className="h-1.5 w-1.5 rounded-full bg-gray-100 ring-1 ring-gray-300" />
																						)}
																					</div>
																					<p className="flex-auto py-0.5 text-xs leading-5 text-gray-500">
																						{generateLineItem(activityItem)}
																					</p>
																					<time
																						dateTime={activityItem?.createdAt}
																						className="flex-none py-0.5 text-xs leading-5 text-gray-500"
																					>
																						{moment(
																							activityItem.createdAt,
																						).fromNow()}
																					</time>
																				</>
																			</li>
																		))}
																</>
															)}

															{tab === 'meta' && (
																<>
																	<div className="mb-3">
																		<div className="text-xs font-medium">
																			Created:
																		</div>
																		<div className="text-xxs text-gray-500 mt-1.5">
																			{moment(ticket?.createdAt).format(
																				'MMM Do, YYYY',
																			)}
																		</div>
																	</div>
																	<div className="mb-3">
																		<div className="text-xs font-medium">
																			Last Updated:
																		</div>
																		<div className="text-xxs text-gray-500 mt-1.5">
																			{moment(ticket?.updatedAt).format(
																				'MMM Do, YYYY',
																			)}
																		</div>
																	</div>
																</>
															)}
														</ul>
													</div>
												</div>
											</div>
										</>
									)}
								</Tabs>
							</section>
						</div>
						<div className="col-span-1 flex flex-col">
							<div className="flex flex-wrap justify-end">
								<Listbox
									value={
										ticket?.status
											? STATUS_OPTIONS.find((o) => o.key === ticket.status).key
											: null
									}
									onChange={(status) =>
										status !== ticket?.status &&
										onUpdate(
											{
												id: open?.id,
												status,
											},
											() => {
												handleClose();
											},
										)
									}
								>
									{({ open }) => (
										<>
											<Listbox.Label className="sr-only">
												Change published status
											</Listbox.Label>
											<div className="relative mr-3">
												<div className="inline-flex divide-x divide-indigo-700 rounded-md shadow-sm">
													<div className="inline-flex items-center gap-x-1.5 rounded-l-md bg-indigo-600 px-3 py-2 text-white shadow-sm">
														<p className="text-xxs font-semibold">
															{ticket?.status
																? STATUS_OPTIONS.find(
																		(o) => o.key === ticket.status,
																	).name
																: 'Unavailable'}
														</p>
													</div>
													<Listbox.Button className="inline-flex items-center rounded-l-none rounded-r-md bg-indigo-600 p-2 hover:bg-indigo-700 focus:outline-none">
														<span className="sr-only">
															Change published status
														</span>
														<ChevronDownIcon
															className="h-3 w-3 text-white"
															aria-hidden="true"
														/>
													</Listbox.Button>
												</div>

												<Transition
													show={open}
													as={Fragment}
													leave="transition ease-in duration-100"
													leaveFrom="opacity-100"
													leaveTo="opacity-0"
												>
													<Listbox.Options className="absolute right-0 z-10 mt-2 w-72 origin-top-right divide-y divide-gray-200 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
														{STATUS_OPTIONS.map((option) => (
															<Listbox.Option
																key={option.key}
																className={({ active, selected }) => {
																	return classNames(
																		selected &&
																			'cursor-default bg-blue-50/50 text-blue-700',
																		'cursor-pointer select-none p-4 text-xxs',
																	);
																}}
																value={option.key}
															>
																{({ selected, active }) => (
																	<div className="flex flex-col">
																		<div className="flex justify-between">
																			<p
																				className={
																					selected
																						? 'font-semibold'
																						: 'font-normal'
																				}
																			>
																				{option.name}
																			</p>
																		</div>
																		<p
																			className={classNames(
																				active && !selected
																					? 'text-gray-900 '
																					: 'text-gray-500',
																				'mt-2',
																			)}
																		>
																			{option.description}
																		</p>
																	</div>
																)}
															</Listbox.Option>
														))}
													</Listbox.Options>
												</Transition>
											</div>
										</>
									)}
								</Listbox>

								{/*<Menu as="div" className="relative inline-block text-left">
									<div>
										<Menu.Button className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-xxs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 items-center">
											Actions
											<ChevronDownIcon
												className="-mr-1 h-3 w-3 text-gray-400"
												aria-hidden="true"
											/>
										</Menu.Button>
									</div>

									<Transition
										as={Fragment}
										enter="transition ease-out duration-100"
										enterFrom="transform opacity-0 scale-95"
										enterTo="transform opacity-100 scale-100"
										leave="transition ease-in duration-75"
										leaveFrom="transform opacity-100 scale-100"
										leaveTo="transform opacity-0 scale-95"
									>
										<Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
											<div className="py-1">
												<Menu.Item>
													{({ active }) => (
														<a
															href="#"
															className={classNames(
																active
																	? 'bg-gray-100 text-gray-900'
																	: 'text-gray-700',
																'block px-4 py-2 text-sm'
															)}
														>
															Edit
														</a>
													)}
												</Menu.Item>
												<Menu.Item>
													{({ active }) => (
														<a
															href="#"
															className={classNames(
																active
																	? 'bg-gray-100 text-gray-900'
																	: 'text-gray-700',
																'block px-4 py-2 text-sm'
															)}
														>
															Duplicate
														</a>
													)}
												</Menu.Item>
											</div>
											<div className="py-1">
												<Menu.Item>
													{({ active }) => (
														<a
															href="#"
															className={classNames(
																active
																	? 'bg-gray-100 text-gray-900'
																	: 'text-gray-700',
																'block px-4 py-2 text-sm'
															)}
														>
															Archive
														</a>
													)}
												</Menu.Item>
												<Menu.Item>
													{({ active }) => (
														<a
															href="#"
															className={classNames(
																active
																	? 'bg-gray-100 text-gray-900'
																	: 'text-gray-700',
																'block px-4 py-2 text-sm'
															)}
														>
															Move
														</a>
													)}
												</Menu.Item>
											</div>
											<div className="py-1">
												<Menu.Item>
													{({ active }) => (
														<a
															href="#"
															className={classNames(
																active
																	? 'bg-gray-100 text-gray-900'
																	: 'text-gray-700',
																'block px-4 py-2 text-sm'
															)}
														>
															Share
														</a>
													)}
												</Menu.Item>
												<Menu.Item>
													{({ active }) => (
														<a
															href="#"
															className={classNames(
																active
																	? 'bg-gray-100 text-gray-900'
																	: 'text-gray-700',
																'block px-4 py-2 text-sm'
															)}
														>
															Add to favorites
														</a>
													)}
												</Menu.Item>
											</div>
											<div className="py-1">
												<Menu.Item>
													{({ active }) => (
														<a
															href="#"
															className={classNames(
																active
																	? 'bg-gray-100 text-gray-900'
																	: 'text-gray-700',
																'block px-4 py-2 text-sm'
															)}
														>
															Delete
														</a>
													)}
												</Menu.Item>
											</div>
										</Menu.Items>
									</Transition>
															</Menu>*/}
							</div>

							<div className="my-3 flex-grow">
								<div className="text-xs font-medium">Assigned to:</div>
								<div className="text-xs text-gray-500 mt-1.5">
									<Editable onClick={() => setEditingModalOpen('assignedTo')}>
										{ticket?.assignedTo ? (
											<div className="cursor-pointer border rounded-md p-2 px-3 text-xs flex items-center">
												<AvatarGenerated
													id={getUserFullName(ticket?.assignedTo)}
													size="xxs"
													className="mr-3"
												/>{' '}
												{getUserFullName(ticket?.assignedTo)}
											</div>
										) : (
											'Unassigned'
										)}
									</Editable>
								</div>
							</div>

							<div className="flex justify-end items-center mt-4">
								<Menu as="div" className="relative inline-block text-left">
									<div className="flex">
										<Menu.Button className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 items-center text-xs text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none">
											Manage
											<ChevronDownIcon
												className="-mr-1 h-3 w-3 text-gray-400"
												aria-hidden="true"
											/>
										</Menu.Button>
										{/*<button
										type="button"
										className="rounded-md bg-white text-gray-400 hover:text-gray-500"
										onClick={() => handleClose()}
									>
										<span className="sr-only">Close</span>
										<XMarkIcon className="h-5 w-5" aria-hidden="true" />
								</button>*/}
									</div>

									<Transition
										as={Fragment}
										enter="transition ease-out duration-100"
										enterFrom="transform opacity-0 scale-95"
										enterTo="transform opacity-100 scale-100"
										leave="transition ease-in duration-75"
										leaveFrom="transform opacity-100 scale-100"
										leaveTo="transform opacity-0 scale-95"
									>
										<Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
											<div className="py-1">
												<Menu.Item>
													{({ active }) => (
														<div
															className={classNames(
																active
																	? 'bg-gray-100 text-gray-900'
																	: 'text-gray-700',
																'block px-4 py-2 text-xs cursor-pointer',
															)}
															onClick={() =>
																onOpenDeleteModal({
																	title:
																		'Are you sure you want to delete this ticket and all of its history?',
																	description: (
																		<>
																			<span>
																				This will delete all historical history
																				of this ticket, including any references
																				in assets.
																			</span>
																		</>
																	),
																	confirmText: 'Delete Ticket',
																	onConfirm: () => {
																		onDeleteTicket({ id: ticket?.id }, () => {
																			onCloseDeleteModal();
																			handleClose();
																		});
																	},
																})
															}
														>
															Delete Ticket
														</div>
													)}
												</Menu.Item>
											</div>
										</Menu.Items>
									</Transition>
								</Menu>
							</div>
						</div>
					</div>
				)}
			</Modal>
			<Modal
				open={!!isEditingModalOpen}
				onClose={() => setEditingModalOpen(false)}
			>
				{isEditingModalOpen === 'description' && (
					<>
						<TemplateTextarea
							name="Description"
							placeholder="Enter a description"
							onChange={(index, description) =>
								setForm({ ...form, description })
							}
							value={form?.description}
							autoFocus
						/>
					</>
				)}
				{isEditingModalOpen === 'subject' && (
					<>
						<TemplateField
							name="Summary"
							placeholder="Enter brief summary"
							onChange={(index, subject) => setForm({ ...form, subject })}
							value={form?.subject}
							autoFocus
							maxLength={50}
							showMaxLength
						/>
					</>
				)}
				{isEditingModalOpen === 'assignedTo' && (
					<>
						<TemplateDropdown
							name="Assigned To"
							placeholder="Select a user..."
							onChange={(index, assignedTo) => setForm({ ...form, assignedTo })}
							value={form?.assignedTo}
							options={[
								{ label: 'Unassigned', value: 'unassigned' },
								...team?.rows
									?.filter((member) => member.active && !member.archived)
									?.map((member) => ({
										label: (
											<div className="flex items-center text-xs">
												<AvatarGenerated
													id={getUserFullName(member)}
													size={'xs'}
													className="mr-3"
												/>
												{`${member?.first_name} ${member?.last_name} (${member?.email})`}
											</div>
										),
										value: member.id,
									})),
							]}
						/>
					</>
				)}

				{isEditingModalOpen === 'severity' && (
					<>
						<TemplateDropdown
							name="Severity"
							options={Object.keys(SeverityMapping).map(
								(key) => SeverityMapping[key],
							)}
							onChange={(index, severity) => setForm({ ...form, severity })}
							value={form?.severity}
						/>
					</>
				)}
				<div className="flex justify-end mt-4">
					<Button
						disabled={isUpdateDisabled()}
						onClick={() =>
							onUpdate(
								{
									id: open?.id,
									...(ticket?.description !== form?.description
										? { description: form?.description }
										: {}),
									...(ticket?.subject !== form?.subject
										? { subject: form?.subject }
										: {}),
									...(ticket?.status !== form?.status
										? { status: form?.status }
										: {}),
									...(ticket?.severity !== form?.severity
										? { severity: form?.severity }
										: {}),
									...(ticket?.assigned_to !== form?.assignedTo
										? { assignedTo: form?.assignedTo }
										: {}),
								},
								() => {
									setEditingModalOpen(false);
									refreshTicket({ id: open?.id }, null, { silent: true });
								},
							)
						}
					>
						{updating === 'pending' ? 'Updating...' : 'Update'}
					</Button>
				</div>
			</Modal>
		</>
	);
};

export default TicketOverview;
